import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminPaymentAgentsAPI, fmtError } from '@/lib/api';
import { Plus, X, Trash2, Power } from 'lucide-react';

const METHOD_OPTS = ['Bank Transfer', 'Cash', 'E-Wallet (USSD)', 'PayPal', 'Mobile Money', 'Skrill', 'Wise', 'Crypto'];

const empty = {
  name: '', description: '', contact_url: '', country: '',
  methods: [], logo_url: '', commission_pct: 0, active: true,
};

export default function AdminPaymentAgents() {
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [err, setErr] = useState('');
  const [appsCount, setAppsCount] = useState(0);

  const load = async () => {
    try {
      const [agents, apps] = await Promise.all([
        adminPaymentAgentsAPI.list(),
        adminPaymentAgentsAPI.apps('pending').catch(() => []),
      ]);
      setRows(agents);
      setAppsCount(apps.length);
      setErr('');
    } catch (e) { setErr(fmtError(e)); }
  };
  useEffect(() => { load(); }, []);

  const toggleMethod = (m) => {
    setForm(f => ({ ...f, methods: f.methods.includes(m) ? f.methods.filter(x => x !== m) : [...f.methods, m] }));
  };

  const submit = async () => {
    setErr('');
    try {
      if (editing) await adminPaymentAgentsAPI.update(editing.agent_id, form);
      else await adminPaymentAgentsAPI.create(form);
      setCreating(false); setEditing(null); setForm(empty); load();
    } catch (e) { setErr(fmtError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm('Permanently delete this agent?')) return;
    try { await adminPaymentAgentsAPI.remove(id); load(); }
    catch (e) { setErr(fmtError(e)); }
  };

  const toggleActive = async (a) => {
    try { await adminPaymentAgentsAPI.update(a.agent_id, { active: !a.active }); load(); }
    catch (e) { setErr(fmtError(e)); }
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      name: a.name || '', description: a.description || '', contact_url: a.contact_url || '',
      country: a.country || '', methods: a.methods || [], logo_url: a.logo_url || '',
      commission_pct: a.commission_pct || 0, active: !!a.active,
    });
    setCreating(true);
    setErr('');
  };

  return (
    <div className="space-y-6" data-testid="admin-payment-agents">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-3xl">Payment Agents</h1>
        <div className="flex gap-2">
          <Link to="/admin/payment-agent-applications" className="btn-ghost btn-sm" data-testid="open-agent-apps">
            Applications {appsCount > 0 && <span className="ml-2 chip chip-yellow">{appsCount}</span>}
          </Link>
          <button className="btn-primary btn-sm" onClick={() => { setForm(empty); setEditing(null); setCreating(true); setErr(''); }} data-testid="new-agent-btn">
            <Plus size={14} /> New agent
          </button>
        </div>
      </div>

      {err && <div className="text-sm text-[#ff3366]" data-testid="agent-err">{err}</div>}

      <div className="card-tech rounded-lg overflow-x-auto">
        <div className="min-w-[1100px]">
          <div className="grid grid-cols-[1.5fr_1fr_1.5fr_1.5fr_1fr_1fr_1.4fr] gap-3 px-4 py-3 label-xs border-b border-white/5">
            <div>NAME</div><div>COUNTRY</div><div>METHODS</div><div>CONTACT</div><div>COMM. %</div><div>ACTIVE</div><div>ACTIONS</div>
          </div>
          {rows.length === 0 && <div className="px-4 py-6 text-sm text-zinc-500">No agents yet — create one above, or review pending applications.</div>}
          {rows.map(a => (
            <div key={a.agent_id} className="grid grid-cols-[1.5fr_1fr_1.5fr_1.5fr_1fr_1fr_1.4fr] gap-3 px-4 py-3 border-b border-white/5 text-sm items-center" data-testid={`agent-row-${a.agent_id}`}>
              <div className="text-white truncate">{a.name}</div>
              <div className="text-zinc-400">{a.country || '—'}</div>
              <div className="text-xs text-zinc-400 truncate">{(a.methods || []).join(' · ')}</div>
              <div className="text-xs font-mono text-[#00ff66] truncate" title={a.contact_url}>{a.contact_url}</div>
              <div className="font-mono">{a.commission_pct ? `${a.commission_pct}%` : '—'}</div>
              <div>
                <button onClick={() => toggleActive(a)} className={`px-2 py-1 rounded text-xs font-mono border ${a.active ? 'border-[#00ff66]/40 text-[#00ff66]' : 'border-white/10 text-zinc-500'}`} data-testid={`agent-active-${a.agent_id}`}>
                  <Power size={11} className="inline mr-1" />{a.active ? 'ON' : 'OFF'}
                </button>
              </div>
              <div className="flex gap-2">
                <button className="btn-ghost btn-sm" onClick={() => openEdit(a)} data-testid={`agent-edit-${a.agent_id}`}>Edit</button>
                <button className="btn-ghost btn-sm text-[#ff3366]" onClick={() => remove(a.agent_id)} data-testid={`agent-del-${a.agent_id}`}><Trash2 size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {creating && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => { setCreating(false); setEditing(null); }}>
          <div className="card-tech rounded-lg p-6 max-w-xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-display text-2xl">{editing ? 'Edit agent' : 'New payment agent'}</h3>
              <button onClick={() => { setCreating(false); setEditing(null); }} className="text-zinc-500 hover:text-white"><X size={18} /></button>
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <div className="label-xs mb-1">Display name</div>
                <input className="input-tech" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="agent-name" />
              </div>
              <div>
                <div className="label-xs mb-1">Country / region</div>
                <input className="input-tech" value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} data-testid="agent-country" />
              </div>
              <div>
                <div className="label-xs mb-1">Commission %</div>
                <input className="input-tech" type="number" step="0.1" value={form.commission_pct} onChange={e => setForm({ ...form, commission_pct: Number(e.target.value) })} data-testid="agent-commission" />
              </div>
              <div className="sm:col-span-2">
                <div className="label-xs mb-1">Contact URL (WhatsApp / Telegram)</div>
                <input className="input-tech" value={form.contact_url} onChange={e => setForm({ ...form, contact_url: e.target.value })} data-testid="agent-contact" />
              </div>
              <div className="sm:col-span-2">
                <div className="label-xs mb-1">Logo URL (optional)</div>
                <input className="input-tech" value={form.logo_url} onChange={e => setForm({ ...form, logo_url: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <div className="label-xs mb-1">Description (markdown-safe text)</div>
                <textarea className="input-tech" rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="agent-description" />
              </div>
              <div className="sm:col-span-2">
                <div className="label-xs mb-2">Payment methods</div>
                <div className="flex flex-wrap gap-2">
                  {METHOD_OPTS.map(m => (
                    <button type="button" key={m} onClick={() => toggleMethod(m)}
                      className={`text-xs font-mono tracking-wider px-3 py-1.5 rounded-full border ${form.methods.includes(m) ? 'border-[#00ff66] bg-[#00ff66]/15 text-[#00ff66]' : 'border-white/10 text-zinc-400'}`}
                    >{m}</button>
                  ))}
                </div>
              </div>
              <label className="sm:col-span-2 flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} />
                <span className="text-sm">Active (visible to traders)</span>
              </label>
            </div>
            {err && <div className="mt-3 text-sm text-[#ff3366]">{err}</div>}
            <div className="mt-4 flex gap-2">
              <button className="btn-primary" onClick={submit} disabled={!form.name || !form.contact_url} data-testid="submit-agent">
                {editing ? 'Save changes' : 'Create agent'}
              </button>
              <button className="btn-ghost" onClick={() => { setCreating(false); setEditing(null); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
